import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {SurveyService} from "../../services/survey.service";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {SaveSurveyInstanceRequest, SurveyErrors, UpdatedQuestion} from "dci-service-shared";
import {firstValueFrom, skip} from "rxjs";
import {SurveyState} from "../../util/survey-state";
import {ActivatedRoute, Router} from "@angular/router";
import {SurveyModule} from "survey-angular-ui";
import {CompletingEvent, CurrentPageChangingEvent, SurveyModel, ValueChangingEvent} from "survey-core";
import {
	SurveyInstance,
	SurveyQuestionAnswers,
	SurveyQuestionFieldName,
	SurveyQuestions,
	SurveyVariables
} from '@allaihealth/survey';
import {SubscriptionHandler} from "../../util/subscription-handler";
import {ModuleLoader} from "../../util/module-loader";

/** Shows the main part of DCI's to users */
@Component({
	selector: 'app-survey-page',
	imports: [
		SurveyModule,
		FormsModule
	],
	templateUrl: './survey-page.component.html',
	styleUrl: './survey-page.component.scss'
})
export class SurveyPageComponent implements OnInit, OnDestroy {
	/** Current state of the survey */
	private state:SurveyState;

	/** Loader for handling the unload/load of survey modules */
	private loader:ModuleLoader;

	/** Promise of a current background save operation */
	private saveInProgress:Promise<void>;

	/** Wait for a page change or complete before starting a loading page */
	private pageChangingBlock:SubscriptionHandler;

	/** Hold all subscriptions that need to be unregistered on destroy */
	private subscriptions:SubscriptionHandler = new SubscriptionHandler();

	/** Questions that have been updated since the last save */
	private updatedQuestions:UpdatedQuestion[];

	/** The currently displayed SurveyJS survey */
	model:SurveyModel;

	constructor(private route:ActivatedRoute,
	            private router:Router,
	            private http:HttpClient,
	            private surveyService:SurveyService) {
		//listen to param map and skip the first one
		this.subscriptions.addSubscription(this.route.paramMap.pipe(skip(1)), paramMap => {
			this.setPageNumberFromRoute(parseInt(paramMap.get("page")));
		});
	}

	async ngOnInit() {
		try {
			this.model = this.surveyService.newSurvey();

			this.model.beginLoading();

			console.info(`Starting load of ${this.surveyService.surveyId}...`);

			let instance = await firstValueFrom(
				this.http.get<SurveyInstance>(`${environment.apiUrl}/dci/${this.surveyService.surveyId}/answers`));

			//if no instance then we need to start fresh
			if(!instance) {
				console.info(`No survey progress found, starting fresh`);

				instance = await firstValueFrom(
					this.http.get<SurveyInstance>(`${environment.apiUrl}/module`));
			}

			console.info("Loaded, initializing survey")

			//set up the state
			this.state = new SurveyState(instance);

			// add partial saving
			this.model.partialSendEnabled = true;

			//add a whole bunch of subscriptions for carious things we need to track
			this.subscriptions.addSubscription(this.model.onPartialSend, () => this.saveProgress());

			this.subscriptions.addSubscription(this.model.onCurrentPageChanged, () => this.setPageFromSurvey());

			this.subscriptions.addSubscription(this.model.onComplete, () => {
				this.completeSurvey();
			});

			this.subscriptions.addSubscription(this.model.onValueChanging, (_sender, options) => {
				this.updateSurveyVariables(options);
			});

			//watch for complete starting (block if there are pending module loads)
			this.subscriptions.addSubscription(this.model.onCompleting,
				(_survey, options) => this.onPageChange(options));

			//watch for a next button click (block if there are pending module loads)
			this.subscriptions.addSubscription(this.model.onCurrentPageChanging,
				(_survey, options) => this.onPageChange(options));

			this.loader = new ModuleLoader(this.http, this.surveyService);

			//set the json and remove previous answers and progress
			this.loadJsonFromState();

			//set the answers
			this.model.data = this.state.answers;

			if(this.state.surveyInstance.pageNo) {
				this.model.currentPageNo = this.state.surveyInstance.pageNo;
			}

			//set the survey's current page
			this.setPageFromSurvey();

			this.model.endLoading();

		} catch(e) {
			console.error("Failed to load survey", e);

			if(e instanceof HttpErrorResponse) {
				console.error("HttpErrorResponse", e.error);
				if(e.error != SurveyErrors.SurveyLoginFailed) {
					await this.router.navigate(['/error', e.error]);
				}
			}
		}
	}

	ngOnDestroy():void {
		this.subscriptions.unsubscribeAll();

		this.pageChangingBlock?.unsubscribeAll();

		this.loader?.dispose();

		this.model.dispose();
	}

	/** Save the survey's progress to the server */
	private async saveProgress() {
		try {
			this.state.update(this.model.data, this.model.currentPageNo);
			this.deleteOrphanAnswers();

			console.info("Saving progress of survey");

			const request:SaveSurveyInstanceRequest = {
				surveyInstance: this.state.surveyInstance,
				updatedQuestions: this.updatedQuestions
			};

			this.updatedQuestions = undefined;

			this.saveInProgress = firstValueFrom(this.http.put<void>(
				`${environment.apiUrl}/dci/${this.surveyService.surveyId}/answers`, request));

			await this.saveInProgress;

			this.saveInProgress = undefined;
		} catch(e) {
			console.error("Failed to save progress", e);
			this.surveyService.redirectToErrorPage();
		}
	}

	/** Set the page number in the browser's route from the survey's current page number */
	private setPageFromSurvey() {
		this.router.navigate([this.surveyService.surveyId, this.model.currentPageNo]);
	}

	/** Set the current page number in the survey from the route */
	private setPageNumberFromRoute(pageNum:number) {
		if(isNaN(pageNum) || pageNum < 0 || pageNum >= this.model.pageCount) {
			//the page number is invalid, force it to set
			this.setPageFromSurvey();
		} else {
			//otherwise set the page number in the survey
			this.model.currentPageNo = pageNum;
		}
	}

	/**
	 * Called when loading in the foreground and the user presses the "next" or "complete" button: the loading screen
	 * will show if the loading isn't already complete, otherwise the load will finish
	 * @param options the options from the survey event to block the button press
	 */
	private async onPageChange(options:CompletingEvent | CurrentPageChangingEvent) {
		//only block if we need to load
		if(this.loader.hasPendingChanges(this.state)) {
			console.info("Pending changes are in the queue, issuing change now");

			options.allow = false;

			const nextPage = "isPrevPage" in options && options.isPrevPage ?
				this.model.currentPageNo - 1 : this.model.currentPageNo + 1;

			await this.doLoadModules(nextPage);
		}
	}

	/**
	 * Actually load in the new modules
	 * @param nextPage what the page number should be when the loading is complete
	 */
	private async doLoadModules(nextPage:number) {
		try {
			this.model.beginLoading();

			//get pending modules to load and unload
			const pending = await this.loader.getPendingChanges();

			//parse the pending changes and if there are some, start altering the survey
			if(this.state.parseChanges(pending)) {
				this.loadJsonFromState();

				this.model.currentPageNo = nextPage;

				//re-run triggers to see if the newly loaded modules have more changes
				this.model.runTriggers();

				this.deleteOrphanAnswers();
			}

			this.model.endLoading();
		} catch(e) {
			console.error("Failed to load/unload new modules");
			this.surveyService.redirectToErrorPage();
			return;
		}

		//if a trigger reload caused more changes, then do another load/unload
		if(this.loader.hasPendingChanges(this.state)) {
			await this.doLoadModules(nextPage);
		} else {
			await this.saveProgress();
		}
	}

	/** Delete answers that no longer exist in the answers tree after a reload */
	private deleteOrphanAnswers() {
		for(const questionName of Object.keys(this.state.answers)) {
			if(!this.model.getQuestionByName(questionName)) {
				delete this.state.answers[questionName];
			}
		}
	}

	/** Complete the survey */
	private async completeSurvey() {
		this.model.beginLoading();

		this.state.update(this.model.data, this.model.currentPageNo);

		const request:SaveSurveyInstanceRequest = {
			surveyInstance: this.state.surveyInstance
		};

		try {
			await this.saveProgress();
			await firstValueFrom(this.http.put<void>(`${environment.apiUrl}/dci/${this.surveyService.surveyId}/complete`, request));
			await this.router.navigate(["/", this.surveyService.surveyId, "csat"]);
		} catch(e) {
			console.error("Failed to complete dci", e);
			this.surveyService.redirectToErrorPage();
		}
	}

	/** Called when the user updates a survey answer */
	private async updateSurveyVariables(options:ValueChangingEvent) {
		//get the special question name if it was one
		const specialQuestion = options.question[SurveyQuestionFieldName] as SurveyQuestions;

		//if it is a special question then update variables (if necessary and add to updated questions)
		if(typeof specialQuestion == "string") {
			switch(specialQuestion) {
				case SurveyQuestions.PatientSex:
					this.state.setSurveyVariable(SurveyVariables.Sex, `"${options.value}"`);
					break;
				case SurveyQuestions.TermsOfService:
					this.state.setSurveyVariable(SurveyVariables.TosComplete, `${options.value[0] == SurveyQuestionAnswers.TermsOfServiceAccept}`);
					break;
			}

			if(!this.updatedQuestions) {
				this.updatedQuestions = [];
			}

			this.updatedQuestions.push({
				questionName: options.question.name,
				specialName: specialQuestion
			});
		}
	}

	/** Update the {@link SurveyModel} with new json from the {@link SurveyState} */
	private loadJsonFromState() {
		//load the changes from the merged survey
		this.model.fromJSON(this.state.buildSurveyJson());

		this.deleteOrphanAnswers();
	}
}
